import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function Terms() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const sectionStyle = {
    marginBottom: '2.5rem',
  };

  const headingStyle = {
    color: '#ffffff',
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1rem',
  };

  const paragraphStyle = {
    color: '#b8d4f1',
    fontSize: '0.95rem',
    lineHeight: 1.8,
    marginBottom: '1rem',
  };

  const listStyle = {
    color: '#a8c5e6',
    fontSize: '0.95rem',
    lineHeight: 1.8,
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '120px 5% 80px',
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      }}>
        <h1 style={{
          fontSize: 'clamp(2rem, 4vw, 2.5rem)',
          fontWeight: 700,
          color: '#ffffff',
          marginBottom: '0.5rem',
        }}>
          Terms of Service
        </h1>
        <p style={{
          color: '#6b9bc9',
          fontSize: '0.9rem',
          marginBottom: '3rem',
        }}>
          Last updated: March 30, 2026
        </p>

        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(74, 158, 255, 0.15)',
          borderRadius: '12px',
          padding: '2.5rem',
        }}>
          <section style={sectionStyle}>
            <h2 style={headingStyle}>1. Agreement to Terms</h2>
            <p style={paragraphStyle}>
              By accessing or using the services provided by Parametrix Capital ("we," "us," or "our"), 
              including our website at parametrixcapital.com and our subscription-based Crypto Market Report, 
              you agree to be bound by these Terms of Service. If you do not agree to these terms, 
              please do not use our services.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>2. Description of Service</h2>
            <p style={paragraphStyle}>
              Parametrix Capital provides market research reports based on algorithmic trading signals 
              and backtested historical data. Our services include:
            </p>
            <ul style={listStyle}>
              <li>Weekly Crypto Market Reports delivered via email</li>
              <li>Entry and exit signals for cryptocurrency assets</li>
              <li>Stop-loss recommendations</li>
              <li>Portfolio performance tracking</li>
              <li>Market analysis and commentary</li>
            </ul>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>3. Not Financial Advice</h2>
            <p style={{...paragraphStyle, fontWeight: 500, color: '#f5a623'}}>
              IMPORTANT: Parametrix Capital does not provide financial advice or any regulated financial service. 
              All content constitutes market research based on backtested historical data only.
            </p>
            <p style={paragraphStyle}>
              Past performance does not guarantee future results. You should consult a qualified financial 
              advisor before making any investment decisions. We are not responsible for any losses incurred 
              from using our signals or recommendations.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>4. Subscription and Payment</h2>
            <p style={paragraphStyle}>
              <strong>Free Trial:</strong> New subscribers receive a 7-day free trial. You will not be charged 
              during the trial period. The trial is available only once per user/email address.
            </p>
            <p style={paragraphStyle}>
              <strong>Billing:</strong> After the trial period, your subscription will automatically renew at 
              the applicable rate (monthly or annual) until cancelled. Payments are processed securely via Stripe.
            </p>
            <p style={paragraphStyle}>
              <strong>Pricing:</strong> Monthly subscription: CHF 25/month. Annual subscription: CHF 250/year.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>5. Refund Policy</h2>
            <p style={{...paragraphStyle, fontWeight: 500}}>
              All sales are final. We do not offer refunds under any circumstances.
            </p>
            <p style={paragraphStyle}>
              You may cancel your subscription at any time, and you will continue to have access until the 
              end of your current billing period. No partial refunds will be provided for unused portions 
              of a subscription period.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>6. Cancellation</h2>
            <p style={paragraphStyle}>
              You may cancel your subscription at any time through the Stripe Customer Portal or by 
              contacting us. Upon cancellation:
            </p>
            <ul style={listStyle}>
              <li>You will retain access until the end of your current billing period</li>
              <li>No further charges will be made after cancellation</li>
              <li>No refunds will be provided for the remaining period</li>
            </ul>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>7. Prohibited Use and Redistribution</h2>
            <p style={{...paragraphStyle, fontWeight: 500, color: '#f5a623'}}>
              Redistribution of our reports is strictly prohibited.
            </p>
            <p style={paragraphStyle}>
              You may not share, forward, publish, or redistribute our reports or any content therein 
              to any third party, including but not limited to:
            </p>
            <ul style={listStyle}>
              <li>Posting on social media platforms (X/Twitter, YouTube, Instagram, etc.)</li>
              <li>Publishing on newsletters, blogs, or websites</li>
              <li>Sharing in trading groups or forums</li>
              <li>Forwarding emails to non-subscribers</li>
              <li>Using our signals to provide services to others</li>
            </ul>
            <p style={paragraphStyle}>
              Each report is watermarked with your subscriber information. Violation of this policy will 
              result in immediate termination of your subscription without refund, and may result in legal action.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>8. Intellectual Property</h2>
            <p style={paragraphStyle}>
              All content, including reports, analysis, signals, algorithms, and website content, is the 
              exclusive property of Parametrix Capital and is protected by copyright and other intellectual 
              property laws. Your subscription grants you a personal, non-transferable license to use our 
              reports for your own investment decisions only.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>9. Limitation of Liability</h2>
            <p style={paragraphStyle}>
              To the maximum extent permitted by law, Parametrix Capital shall not be liable for any 
              indirect, incidental, special, consequential, or punitive damages, including but not limited 
              to loss of profits, data, or other intangible losses, resulting from:
            </p>
            <ul style={listStyle}>
              <li>Your use or inability to use our services</li>
              <li>Any trading or investment decisions made based on our reports</li>
              <li>Unauthorized access to your account</li>
              <li>Any errors or omissions in our content</li>
            </ul>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>10. Governing Law</h2>
            <p style={paragraphStyle}>
              These Terms shall be governed by and construed in accordance with the laws of Switzerland, 
              without regard to its conflict of law provisions. Any disputes arising from these Terms shall 
              be subject to the exclusive jurisdiction of the courts of Zurich, Switzerland.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={headingStyle}>11. Changes to Terms</h2>
            <p style={paragraphStyle}>
              We reserve the right to modify these Terms at any time. We will notify subscribers of any 
              material changes via email. Your continued use of our services after such modifications 
              constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section style={{...sectionStyle, marginBottom: 0}}>
            <h2 style={headingStyle}>12. Contact</h2>
            <p style={paragraphStyle}>
              For questions about these Terms, please contact us at{' '}
              <Link to="/contact" style={{ color: '#4a9eff', textDecoration: 'none' }}>
                our contact page
              </Link>.
            </p>
          </section>
        </div>

        <div style={{
          marginTop: '2rem',
          textAlign: 'center',
        }}>
          <Link
            to="/subscription"
            style={{
              color: '#4a9eff',
              textDecoration: 'none',
              fontSize: '0.9rem',
            }}
          >
            ← Back to Subscription
          </Link>
        </div>
      </div>
    </div>
  );
}
